import { ElementRef, PropsWithChildren, useReducer, useRef, useState } from 'react';
import { css } from '@emotion/react';
import useResizeObserver from '@react-hook/resize-observer';
import { AnimatePresence, motion } from 'motion/react';
import { transparentize } from '@frontend/colors';
import { useEventListener } from '@frontend/event';
import { useTranslation } from '@frontend/i18n';
import { debounce } from '@frontend/timer';
import { theme } from '@frontend/theme';
import { useAlert } from '@frontend/design-system';
import { isUUID } from '../../../views/Calendar/utils';
import { useCalendarView, useCalendarViewProps } from '../providers';
import { initialTimelineState, timelineReducer } from '../reducers';
import { checkIntersection, getTimestamp, zIndex } from '../utils';
import { TimeTooltip } from './time-tooltip';

type EventTimelineWrapper = {
  providerName?: string;
  providerId?: string;
  locationId?: string;
  calendarDateValue?: string;
  isRegisterResizeObserver?: boolean;
};

export const EventTimelineWrapper = ({
  children,
  providerName,
  providerId,
  locationId,
  calendarDateValue,
  isRegisterResizeObserver,
}: PropsWithChildren<EventTimelineWrapper>) => {
  const { startHour, endHour, onEventCreate, shouldAllowEventCreation } = useCalendarViewProps();
  const { eventCarouselData, setVisibleLaneCountBasedOnTotalWidth } = useCalendarView([
    'eventCarouselData',
    'setVisibleLaneCountBasedOnTotalWidth',
  ]);
  const [{ isActivated: isEventActivated, eventStartX, eventStartTime, eventEndX, eventEndTime }, dispatch] =
    useReducer(timelineReducer, initialTimelineState);

  // NOTE: Hiding the intersection restriction for now until we make decision on how to handle different use cases
  const shouldCheckIntersection = false;

  const isActivated = isEventActivated && !eventCarouselData.isOpen;
  const [isIntersecting, setIntersecting] = useState(false);
  const alert = useAlert();
  const { t } = useTranslation('scheduleCalendarViewMain');

  const timelineRef = useRef<ElementRef<'section'>>(null);
  const eventBoxRef = useRef<ElementRef<'div'>>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!shouldAllowEventCreation) return;
    const shouldRound = !e.shiftKey;
    const timestamp = getTimestamp({
      event: e,
      element: timelineRef.current,
      nearestQuarter: shouldRound,
      startHour,
      endHour,
      nearestIncrement: 10,
    });

    if (!timestamp) return;

    const { formattedTime, totalMinutes } = timestamp;

    dispatch({
      type: 'ACTIVATE',
      payload: {
        eventStartX: totalMinutes,
        eventEndX: totalMinutes,
        eventStartTime: formattedTime,
        eventEndTime: formattedTime,
      },
    });
  };

  function handleMouseMove(e: MouseEvent) {
    if (!eventBoxRef.current || !timelineRef.current || !shouldAllowEventCreation) return;

    if (shouldCheckIntersection) {
      setIntersecting(
        checkIntersection({
          targetElement: eventBoxRef.current,
          containerElement: timelineRef.current,
        })
      );
    }

    const shouldRound = !e.shiftKey;

    const timestamp = getTimestamp({
      event: e,
      element: timelineRef.current,
      nearestQuarter: shouldRound,
      startHour,
      endHour,
      nearestIncrement: 10,
    });

    if (!timestamp) return;

    const { formattedTime, totalMinutes } = timestamp;

    dispatch({
      type: 'UPDATE_POSITION',
      payload: {
        eventEndX: totalMinutes,
        eventEndTime: formattedTime,
      },
    });
  }

  const handleMouseUp = () => {
    if (!!providerId && !isUUID(providerId ?? '') && shouldAllowEventCreation) {
      !isIntersecting && alert.error(t('Event cannot be created. This provider does not have a provider ID'));
    } else {
      onEventCreate?.(
        eventStartX > eventEndX ? eventEndTime : eventStartTime,
        eventStartX > eventEndX ? eventStartTime : eventEndTime,
        providerName,
        providerId,
        !isIntersecting,
        locationId ?? '',
        calendarDateValue ?? ''
      );
    }

    dispatch({
      type: 'DEACTIVATE',
    });
  };

  useEventListener('mousemove', handleMouseMove, isActivated);
  useEventListener('mouseup', handleMouseUp, isActivated);

  // Observe only provider timeline for resize to update event-cards width (and placement in event group)
  useResizeObserver(
    isRegisterResizeObserver ? timelineRef : null,
    debounce((entry) => setVisibleLaneCountBasedOnTotalWidth(Math.round(entry.contentRect.width)), 100)
  );

  const disableEventCreate = isIntersecting || (!!providerId && !isUUID(providerId ?? ''));

  return (
    <section ref={timelineRef} onMouseDown={handleMouseDown} css={styles.wrapper}>
      {children}
      <AnimatePresence>
        {isActivated && (
          <motion.div
            ref={eventBoxRef}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              background: disableEventCreate ? transparentize(0.2, theme.colors.critical20) : theme.colors.primary20,
              border: `1px dashed ${
                disableEventCreate ? transparentize(0.2, theme.colors.critical50) : theme.colors.primary40
              }`,
            }}
            exit={{
              opacity: 0,
              scale: 1.1,
            }}
            transition={{ duration: 0.3 }}
            css={styles.creatorCard({
              top: Math.min(eventStartX, eventEndX),
              height: Math.abs(eventStartX - eventEndX),
              isIntersecting,
            })}
          >
            <motion.div css={styles.tooltipContainer}>
              <TimeTooltip>
                {eventEndX - eventStartX < 0
                  ? `${eventEndTime} - ${eventStartTime}`
                  : `${eventStartTime} - ${eventEndTime}`}
              </TimeTooltip>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const styles = {
  wrapper: css({
    width: '100%',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      transition: '0.2s ease',
      opacity: 0,
      backgroundColor: transparentize(0.5, theme.colors.primary10),
      borderLeft: `1px dashed ${theme.colors.primary20}`,
      borderRight: `1px dashed ${theme.colors.primary20}`,
      height: 'calc(100% + 40px)',
      marginTop: '-40px',
      width: '100%',
      zIndex: zIndex.providerTimeLineWrapperHover,
    },
    '&:hover::after': {
      opacity: 1,
    },
  }),
  tooltipContainer: css({
    pointerEvents: 'none',
    position: 'absolute',
    left: '50%',
    top: '-60px',
    transform: 'translateX(-50%)',
    width: 'auto',
  }),
  creatorCard: ({ top, height, isIntersecting }: { top: number; height: number; isIntersecting: boolean }) =>
    css({
      position: 'absolute',
      zIndex: zIndex.eventCreatorCard,
      borderRadius: theme.borderRadius.small,
      userSelect: 'none',
      background: isIntersecting ? theme.colors.critical20 : theme.colors.primary20,
      border: `1px solid ${isIntersecting ? theme.colors.critical50 : theme.colors.primary40}`,
      top,
      height,
      width: '100%',
    }),
};
